(() => {
  'use strict';

  // A browser preference, independent of consent and of the visitor's IP.
  const internalKey = 'cto_internal';
  const internalDomain = location.hostname === 'confie-ta-omra.com' || location.hostname.endsWith('.confie-ta-omra.com') ? '; Domain=confie-ta-omra.com' : '';
  const internalParameter = new URLSearchParams(location.search).get(internalKey);
  if (internalParameter === '1' || internalParameter === '0') {
    try {
      if (internalParameter === '1') localStorage.setItem(internalKey, '1');
      else localStorage.removeItem(internalKey);
    } catch {}
    document.cookie = `${internalKey}=${internalParameter === '1' ? '1' : ''}; Path=/; Max-Age=${internalParameter === '1' ? 31536000 : 0}; SameSite=Lax; Secure${internalDomain}`;
  }
  const isInternal = () => {
    if (document.cookie.split('; ').some(part => part === internalKey + '=1')) return true;
    try { return localStorage.getItem(internalKey) === '1'; } catch { return false; }
  };
  if (isInternal()) {
    // Renew the shared cookie so the checkout pixel sees the same preference.
    document.cookie = `${internalKey}=1; Path=/; Max-Age=31536000; SameSite=Lax; Secure${internalDomain}`;
    for (const key of ['cto_ph_id', 'cto_ph_context']) document.cookie = `${key}=; Path=/; Max-Age=0; SameSite=Lax; Secure${internalDomain}`;
    try { for (const key of ['cto_quote_pending', 'cto_attribution', 'cto_entry_path']) sessionStorage.removeItem(key); } catch {}
    window.posthog?.stopSessionRecording?.();
    window.posthog?.opt_out_capturing?.({ clear_persistence: true });
    window.CTOAnalytics = { capture() {}, internal: true };
    const notice = document.createElement('aside');
    notice.setAttribute('role', 'img');
    notice.setAttribute('aria-label', 'PostHog désactivé — mode interne');
    notice.setAttribute('title', 'PostHog désactivé — mode interne');
    notice.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m4 15-1-4 4 1-1-5 4 2 1-5 3 4 3-3 1 5 3 5-4 4H7z"/><path d="M16 13h.01M3 3l18 18"/></svg>';
    notice.style.cssText = 'position:fixed;bottom:10px;left:10px;z-index:2147483647;width:24px;height:24px;display:grid;place-items:center;background:#173c35;color:#fff;border-radius:50%;opacity:.7';
    document.body.append(notice);
    return;
  }

  const configNode = document.getElementById('CTOAnalyticsConfig');
  if (!configNode || window.CTOAnalytics) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  if (!/^phc_[a-zA-Z0-9]+$/.test(config.token || '')) return;

  const privatePath = /\/(?:account|accounts|cart|checkouts?|orders)(?:\/|$)|\/pages\/(?:saisir-passeports|retour-experience|suivi[^/]*|client-questionnaire)(?:\/|$)/i;
  if (privatePath.test(location.pathname)) return;

  const identityCookie = 'cto_ph_id';
  const pendingKey = 'cto_quote_pending';
  const attributionKey = 'cto_attribution';
  const events = new Set(['gift_clicked', 'ebook_requested', 'ebook_download_clicked', 'ebook_marketing_opt_in', 'service_viewed', 'product_viewed', 'quote_request_started', 'quote_step_viewed', 'quote_request_submitted', 'whatsapp_clicked', 'phone_clicked', 'product_added_to_cart', 'form_error', 'tool_used', 'editorial_link_clicked', 'editorial_interaction', 'partner_clicked']);
  const fields = new Set(['service', 'step', 'product_id', 'variant_id', 'quantity', 'value', 'currency', 'placement', 'error_code', 'field', 'request_id', 'tool_type', 'tool_status', 'destination', 'partner']);
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const safeReplayParams = new Set([...campaignKeys, 'variant', 'preview_theme_id', 'view', 'cto_test', 'cto_internal', 'contact_posted']);
  const parameters = new URLSearchParams(location.search);
  const testMode = config.environment !== 'production' || parameters.get('cto_test') === '1' || parameters.has('preview_theme_id') || window.Shopify?.designMode === true || ['unpublished', 'development'].includes(window.Shopify?.theme?.role);
  const unsafeQuery = [...parameters].some(([key, value]) => !safeReplayParams.has(key) || /@/.test(value));
  const allowReplay = config.replays === true && !unsafeQuery && !testMode && location.pathname !== '/pages/demander-un-devis';
  let instance;
  let loading = false;
  let permitted = false;
  let pageCaptured = false;
  let quoteStarted = false;
  let withdrawn = false;
  let queued = [];

  const consent = () => !isInternal() && (config.forceAnalyticsForTesting === true || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true);
  const cleanPath = value => {
    try { const url = new URL(value, location.origin); return url.origin + url.pathname; } catch { return ''; }
  };
  // Preserve layout without exporting arbitrary data-* attributes or input values.
  const replayAttribute = (name, value) => {
    if (name === '_cssText') return value;
    if (/^rr_(?:width|height|scrollLeft|scrollTop|mediaState|currentTime|paused|volume|muted|playbackRate)$/.test(name)) return value;
    if (/^(?:value|placeholder|title|alt|aria-label|aria-description|data-)/i.test(name)) return '';
    if (/^(?:href|action|formaction)$/i.test(name)) {
      if (/^#[a-z0-9_-]+$/i.test(value)) return value;
      try { const url = new URL(value, location.origin); return /^https?:$/.test(url.protocol) ? url.origin + url.pathname : ''; } catch { return ''; }
    }
    if (!/^(?:class|id|style|src|srcset|sizes|rel|media|type|as|crossorigin|integrity|width|height|viewBox|d|fill|stroke|stroke-width|xmlns|role|aria-hidden|aria-expanded|aria-controls|aria-labelledby|hidden|open|dir|lang|loading|decoding|disabled|checked|selected|multiple|for|tabindex|preserveAspectRatio|points|x|y|x1|x2|y1|y2|cx|cy|r|rx|ry|transform|offset|stop-color|stop-opacity|opacity|clip-path|mask)$/i.test(name)) return '';
    return /@|(?:access_token|passport|beneficiar|password)=/i.test(value) ? '' : value;
  };
  const safeCampaign = value => typeof value === 'string' && value.length <= 100 && !/@|\d{7,}/.test(value) ? value.replace(/[^\p{L}\p{N} _.,:-]/gu, '') : '';
  const storageGet = key => { try { return sessionStorage.getItem(key); } catch { return null; } };
  const storageSet = (key, value) => { try { sessionStorage.setItem(key, value); } catch {} };
  const storageRemove = key => { try { sessionStorage.removeItem(key); } catch {} };
  const cookieDomain = location.hostname === 'confie-ta-omra.com' || location.hostname.endsWith('.confie-ta-omra.com') ? '; Domain=confie-ta-omra.com' : '';
  const setCookie = (value, age) => { document.cookie = `${identityCookie}=${value}; Path=/; Max-Age=${age}; SameSite=Lax; Secure${cookieDomain}`; };
  const randomId = () => crypto.randomUUID();
  const getIdentity = () => {
    const value = document.cookie.split('; ').find(part => part.startsWith(identityCookie + '='))?.split('=')[1];
    if (/^[a-f0-9-]{36}$/i.test(value || '')) return value;
    const created = randomId();
    setCookie(created, 2592000);
    return created;
  };
  const service = () => {
    const inquiry = [...document.querySelectorAll('[data-cto-inquiry]')].find(node => !node.closest('dialog') || node.closest('dialog').open);
    const pageService = [...document.querySelectorAll('[data-cto-service]')].find(node => !node.closest('dialog'));
    const value = inquiry?.dataset.ctoService || pageService?.dataset.ctoService || '';
    return ({ 'guide-omra': 'guide', transferts: 'transfert', visites: 'visite', 'visa-omra': 'visa', 'omra-ramadan-2027': 'omra-ramadan', 'services-sur-place': 'autre', voyager: 'omra' })[value] || value;
  };
  const campaign = () => {
    let saved = {};
    try { saved = JSON.parse(storageGet(attributionKey) || '{}'); } catch {}
    for (const key of campaignKeys) {
      const value = safeCampaign(parameters.get(key));
      if (value) saved[key] = value;
    }
    const result = Object.fromEntries(campaignKeys.map(key => [key, safeCampaign(saved[key])]).filter(([, value]) => value));
    storageSet(attributionKey, JSON.stringify(result));
    return result;
  };
  const context = () => {
    const entry = storageGet('cto_entry_path') || location.pathname;
    storageSet('cto_entry_path', entry);
    const result = { ...campaign(), entry_path: entry, test_mode: testMode };
    document.cookie = `cto_ph_context=${encodeURIComponent(JSON.stringify(result))}; Path=/; Max-Age=1800; SameSite=Lax; Secure${cookieDomain}`;
    return result;
  };

  const sanitize = event => {
    if (!permitted || !consent()) return null;
    const props = event.properties || {};
    if (Array.isArray(props.$elements)) props.$elements = props.$elements.map(element => ({
      tag_name: /^[a-z0-9-]+$/i.test(element.tag_name || '') ? element.tag_name : 'unknown',
      ...(Number.isInteger(element.nth_child) ? { nth_child: element.nth_child } : {}),
      ...(Number.isInteger(element.nth_of_type) ? { nth_of_type: element.nth_of_type } : {})
    }));
    if (props.$heatmap_data) props.$heatmap_data = Object.fromEntries(Object.entries(props.$heatmap_data).map(([url, points]) => [cleanPath(url), points]));
    for (const key of Object.keys(props)) if (/^\$web_vitals_.*_event$/.test(key)) delete props[key];
    delete props.$set;
    delete props.$set_once;
    delete props.$ip;
    delete props.$initial_person_info;
    for (const key of Object.keys(props)) {
      if (/email|phone|first_name|last_name|address|passport|beneficiar|intention|\$exception|\$el_text|\$elements_chain/i.test(key)) delete props[key];
      else if (/url|referrer/i.test(key) && typeof props[key] === 'string') {
        try { const url = new URL(props[key], location.origin); props[key] = /referrer/i.test(key) ? url.origin : url.origin + url.pathname; } catch { delete props[key]; }
      } else if (/utm_/i.test(key)) props[key] = safeCampaign(props[key]);
    }
    props.environment = config.environment;
    props.test_mode = testMode;
    event.properties = props;
    return event;
  };

  const capture = (name, properties = {}) => {
    if (!permitted || !consent() || !events.has(name)) return;
    if (name === 'tool_used') {
      if (!['budget', 'visa_fees', 'visa_checklist'].includes(properties.tool_type) || !['completed', 'reset'].includes(properties.tool_status)) return;
      properties = { tool_type: properties.tool_type, tool_status: properties.tool_status };
    }
    if (name === 'editorial_link_clicked') {
      if (!['hajj_offers', 'nusuk', 'budget', 'preparation'].includes(properties.destination)) return;
      properties = {destination: properties.destination, placement: 'content'};
    }
    if (name === 'editorial_interaction') {
      if (!/^faq_[1-6]$/.test(properties.placement || '')) return;
      properties = {placement: properties.placement};
    }
    if (name === 'partner_clicked') {
      return;
      properties = {partner: properties.partner, placement: 'content'};
    }
    const safe = {};
    for (const [key, value] of Object.entries(properties)) {
      if (!fields.has(key)) continue;
      if (typeof value === 'number' && Number.isFinite(value)) safe[key] = value;
      else if (typeof value === 'string' && value.length <= 100 && !/@/.test(value)) safe[key] = value;
    }
    const payload = { ...context(), page_path: location.pathname, page_type: config.pageType, service: service(), ...safe, environment: config.environment, test_mode: testMode };
    if (instance) instance.capture(name, payload);
    else if (queued.length < 20) queued.push([name, payload]);
  };
  window.CTOAnalytics = { capture };

  const capturePage = () => {
    if (pageCaptured || !instance || !consent()) return;
    pageCaptured = true;
    instance.capture('$pageview', { ...context(), $current_url: cleanPath(location.href), page_path: location.pathname, page_type: config.pageType, environment: config.environment, test_mode: testMode });
    if (service()) capture('service_viewed');
    if (config.productId) capture('product_viewed', { product_id: String(config.productId) });
    const success = document.querySelector('[data-cto-inquiry-success]');
    const failure = document.querySelector('[data-cto-inquiry-error]');
    const stored = storageGet(pendingKey);
    let pending;
    try { pending = JSON.parse(stored || 'null'); } catch {}
    if (pending?.path === location.pathname && Date.now() - pending.time < 3600000) {
      if (success) capture('quote_request_submitted', { request_id: pending.id, service: pending.service });
      if (failure) capture('form_error', { error_code: 'server_validation', service: pending.service });
      if (success || failure) storageRemove(pendingKey);
    }
  };

  const load = () => {
    if (loading || instance) { capturePage(); return; }
    loading = true;
    const options = {
      api_host: config.host,
      ui_host: 'https://eu.posthog.com',
      defaults: '2026-05-30',
      bootstrap: { distinctID: getIdentity(), isIdentifiedID: false },
      person_profiles: 'identified_only',
      persistence: 'cookie',
      cookie_expiration: 30,
      autocapture: { capture_copied_text: false, css_selector_ignorelist: ['.ph-no-autocapture', '[data-ph-no-autocapture]', '[data-cto-private]', '[data-badal-configurator]', '.badal-order-tracking'] },
      mask_all_text: true,
      mask_all_element_attributes: true,
      capture_pageview: false,
      capture_pageleave: true,
      capture_dead_clicks: true,
      capture_exceptions: false,
      capture_performance: { web_vitals: true, web_vitals_attribution: false, network_timing: false },
      capture_heatmaps: !testMode && !unsafeQuery,
      request_queue_config: { flush_interval_ms: 1000 },
      disable_capture_url_hashes: true,
      disable_surveys: true,
      enable_recording_console_log: false,
      disable_session_recording: !allowReplay,
      session_recording: {
        maskAllInputs: true,
        sampleRate: 1,
        maskTextSelector: 'form, [contenteditable], .ph-mask, [data-cto-private]',
        maskAllElementAttributes: false,
        maskAttributeFn: replayAttribute,
        inlineStylesheet: true,
        collectFonts: true,
        captureJsonLd: false,
        blockSelector: '[data-badal-configurator], .badal-order-tracking, [data-cto-private], .ph-no-capture, video, canvas, iframe',
        recordHeaders: false,
        recordBody: false,
        recordCanvas: false,
        streamNetworkBody: false
      },
      before_send: sanitize,
      loaded: ph => {
        instance = ph;
        if (!permitted || !consent()) {
          instance.opt_out_capturing({ clear_persistence: true });
          instance.set_config({ disable_persistence: true });
          return;
        }
        if (withdrawn) {
          instance.set_config({ disable_persistence: false });
          instance.reset();
          withdrawn = false;
        }
        instance.opt_in_capturing({ captureEventName: null });
        if (allowReplay) instance.startSessionRecording({ sampling: true });
        setCookie(instance.get_distinct_id(), 2592000);
        capturePage();
        for (const [name, props] of queued) instance.capture(name, props);
        queued = [];
      }
    };
    if (window.posthog?.init && !Array.isArray(window.posthog)) {
      instance = window.posthog;
      instance.set_config(options);
      instance.opt_in_capturing({ captureEventName: null });
      capturePage();
      return;
    }
    window.posthog = window.posthog || [];
    window.posthog._i = [[config.token, options, 'posthog']];
    window.posthog.__SV = 1;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.src = options.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
    script.onerror = () => { loading = false; queued = []; };
    document.head.append(script);
  };

  const updateConsent = () => {
    permitted = consent();
    if (permitted) {
      if (instance) {
        if (withdrawn) {
          instance.set_config({ disable_persistence: false });
          instance.reset();
          setCookie(instance.get_distinct_id(), 2592000);
          withdrawn = false;
        }
        instance.opt_in_capturing({ captureEventName: null });
        if (allowReplay) instance.startSessionRecording();
      }
      load();
    } else {
      if (instance || loading) withdrawn = true;
      instance?.stopSessionRecording();
      instance?.opt_out_capturing({ clear_persistence: true });
      instance?.set_config({ disable_persistence: true });
      setCookie('', 0);
      storageRemove(pendingKey);
      storageRemove(attributionKey);
      storageRemove('cto_entry_path');
      document.cookie = `cto_ph_context=; Path=/; Max-Age=0; SameSite=Lax; Secure${cookieDomain}`;
      queued = [];
      pageCaptured = false;
      quoteStarted = false;
    }
  };

  document.addEventListener('visitorConsentCollected', updateConsent);
  window.addEventListener?.('storage', event => { if (event.key === internalKey) updateConsent(); });
  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => { if (!error) updateConsent(); });
  } else updateConsent();

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    try {
      const url = new URL(link.href, location.origin);
      const placement = link.closest('cto-team-contact') ? 'team_contact' : link.closest('footer') ? 'footer' : link.closest('header') ? 'header' : 'content';
      if (url.hostname === 'wa.me' || url.hostname === 'api.whatsapp.com') {
        capture('whatsapp_clicked', { placement });
      } else if (url.protocol === 'tel:') {
        capture('phone_clicked', { placement });
      }
    } catch {}
  });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (link?.getAttribute('href') === '/pages/cadeaux') capture('gift_clicked', { placement: link.closest('header') ? 'header' : 'content' });
    if (link?.dataset.ctoGuideDownload !== undefined) capture('ebook_download_clicked', { placement: 'guide' });
    if (link?.dataset.ctoEditorialLink) capture('editorial_link_clicked', {destination: link.dataset.ctoEditorialLink});
    if (link?.dataset.ctoPartner) capture('partner_clicked', {partner: link.dataset.ctoPartner});
  });
  document.addEventListener('toggle', event => {
    const faq = event.target;
    if (faq.open && faq.dataset?.ctoHajjFaq) capture('editorial_interaction', {placement: `faq_${faq.dataset.ctoHajjFaq}`});
  }, true);
  document.addEventListener('cto:guide-requested', () => capture('ebook_requested'));
  document.addEventListener('cto:guide-marketing-opt-in', () => capture('ebook_marketing_opt_in'));
  document.addEventListener('focusin', event => {
    if (!quoteStarted && event.target.closest?.('[data-cto-inquiry]') && consent()) {
      quoteStarted = true;
      capture('quote_request_started');
    }
  });
  document.addEventListener('submit', event => {
    const wrapper = event.target.closest?.('[data-cto-inquiry]');
    if (wrapper?.dataset.projectReady && wrapper.dataset.projectStep !== '4') return;
    if (wrapper && permitted && consent()) storageSet(pendingKey, JSON.stringify({ id: randomId(), path: location.pathname, service: wrapper.dataset.ctoService, time: Date.now() }));
  });
  document.addEventListener('invalid', event => {
    if (event.target.closest?.('[data-cto-inquiry]')) capture('form_error', { error_code: 'validation', field: event.target.type || 'field' });
  }, true);
})();
